import { useEffect, useState, useRef } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BottomNav from "@/components/BottomNav";
import DraftBanner from "@/components/DraftBanner";
import useFormDraft from "@/lib/useFormDraft";
import { ArrowLeft, Upload, Sparkles, Loader2, Trash2, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const DRAFT_KEY = "ica_draft_character";

const empty = {
  name: "",
  tagline: "",
  description: "",
  greeting: "",
  personality: "",
  scenario: "",
  example_dialogue: "",
  avatar_url: "",
  wallpaper_url: "",
  tags: [],
  is_nsfw: false,
  is_public: true,
};

export default function CreateCharacter() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  // Drafts only apply to CREATE (not edit — edit hydrates from server).
  const { form, setForm, restoredAt, clearDraft, discardDraft } =
    useFormDraft(DRAFT_KEY, empty, { enabled: !isEdit });
  const [tagInput, setTagInput] = useState("");
  const [imgPrompt, setImgPrompt] = useState("");
  const [wpPrompt, setWpPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [wpGenerating, setWpGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const wpFileRef = useRef(null);

  useEffect(() => {
    if (isEdit) {
      api.get(`/characters/${editId}`)
        .then(({ data }) => {
          // Strict creator-only guard: only the character's creator can open the editor.
          if (data.creator_id && user?.id && data.creator_id !== user.id) {
            toast.error("You can only edit characters you created.");
            nav(`/character/${data.id}`, { replace: true });
            return;
          }
          setForm({ ...empty, ...data, tags: data.tags || [] });
        })
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Character not found."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, isEdit, user, nav]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  async function onUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image too large (max 4MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, avatar_url: reader.result }));
    reader.readAsDataURL(file);
  }

  async function generateImage() {
    if (!imgPrompt.trim()) {
      toast.error("Enter a description first.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/generate/avatar", { prompt: imgPrompt });
      setForm((f) => ({ ...f, avatar_url: data.avatar_url }));
      toast.success("Portrait generated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  }

  function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
    setTagInput("");
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Strip server-managed fields
      delete payload.id; delete payload.creator_id; delete payload.creator_username; delete payload.interactions; delete payload.created_at;
      if (isEdit) {
        const { data } = await api.put(`/characters/${editId}`, payload);
        toast.success(`${data.name} updated.`);
        nav(`/character/${data.id}`, { replace: true });
      } else {
        const { data } = await api.post("/characters", payload);
        toast.success(`${data.name} created.`);
        clearDraft();  // draft is now permanently persisted server-side
        nav(`/character/${data.id}`, { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function del() {
    if (!isEdit) return;
    if (!confirm(`Delete ${form.name}? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/characters/${editId}`);
      toast.success("Character deleted.");
      nav("/discover", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-safe-6">
      <header className="mb-6 flex items-center gap-3">
        <button
          data-testid="create-back-btn"
          onClick={() => nav(-1)}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <div className="font-display text-xs uppercase tracking-[0.35em] text-violet-400/80">{isEdit ? "Edit" : "New"}</div>
          <h1 className="font-display text-3xl leading-tight text-white">{isEdit ? "Edit Character" : "Create Character"}</h1>
        </div>
      </header>

      <DraftBanner
        restoredAt={restoredAt}
        onDiscard={discardDraft}
        testId="create-character-draft-banner"
      />

      <form onSubmit={submit} className="space-y-5" data-testid="create-character-form">
        <Section title="Portrait">
          <div className="flex items-start gap-4">
            <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-white/5">
              {form.avatar_url ? (
                <img src={form.avatar_url} alt="avatar" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-display text-3xl text-white/30">
                  {form.name?.[0] || "?"}
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <label className="btn-pill btn-ghost w-full cursor-pointer" data-testid="create-upload-label">
                <Upload size={16} />
                Upload image
                <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="create-upload-input" />
              </label>
              <div className="flex gap-2">
                <input
                  data-testid="create-img-prompt-input"
                  value={imgPrompt}
                  onChange={(e) => setImgPrompt(e.target.value)}
                  placeholder="Describe the character to generate…"
                  className="input-field flex-1 !py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={generateImage}
                  disabled={generating}
                  data-testid="create-generate-btn"
                  className="btn-pill btn-primary text-sm px-4 disabled:opacity-50"
                >
                  {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  {generating ? "" : "Imagine"}
                </button>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Basics">
          <Field label="Name">
            <input required data-testid="create-name-input" value={form.name} onChange={set("name")} className="input-field" />
          </Field>
          <Field label="Tagline">
            <input data-testid="create-tagline-input" value={form.tagline} onChange={set("tagline")} placeholder="Short punchy hook." className="input-field" />
          </Field>
        </Section>

        <Section title="Character depth">
          <Field label="Description">
            <AutoTextarea data-testid="create-description-input" value={form.description} onChange={set("description")} minRows={4} placeholder="Physical appearance, backstory, quirks…" className="input-field" />
          </Field>
          <Field label="Personality">
            <AutoTextarea data-testid="create-personality-input" value={form.personality} onChange={set("personality")} minRows={3} placeholder="Traits, mannerisms, tone of speech." className="input-field" />
          </Field>
          <Field label="Opening scenario">
            <AutoTextarea data-testid="create-scenario-input" value={form.scenario} onChange={set("scenario")} minRows={3} placeholder="Set the scene for the first message." className="input-field" />
          </Field>
          <Field label="Greeting (first message from character)">
            <AutoTextarea data-testid="create-greeting-input" value={form.greeting} onChange={set("greeting")} minRows={4} placeholder="*She looks up as you enter…*" className="input-field" />
          </Field>
          <Field label="Example dialogue (optional)">
            <AutoTextarea data-testid="create-example-input" value={form.example_dialogue} onChange={set("example_dialogue")} minRows={3} placeholder="User: ...\nCharacter: ..." className="input-field" />
          </Field>
        </Section>

        <Section title="Chat wallpaper (optional)">
          <p className="mb-3 text-xs text-neutral-500">A cinematic backdrop that fades behind the chat when roleplaying with this character. Skip to use plain dark background.</p>
          {form.wallpaper_url && (
            <div className="mb-3 relative overflow-hidden rounded-xl border border-white/10">
              <img
                data-testid="create-wallpaper-preview"
                src={form.wallpaper_url}
                alt="Wallpaper preview"
                className="max-h-40 w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0B0A10]/80 via-[#0B0A10]/30 to-transparent" />
              <button
                type="button"
                data-testid="create-wallpaper-clear"
                onClick={() => setForm({ ...form, wallpaper_url: "" })}
                className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white/80 hover:bg-black/80"
                aria-label="Remove wallpaper"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              data-testid="create-wallpaper-prompt-input"
              value={wpPrompt}
              onChange={(e) => setWpPrompt(e.target.value)}
              placeholder="Describe the scene (e.g. moonlit forest clearing)"
              className="input-field flex-1 !py-2"
            />
            <button
              type="button"
              data-testid="create-wallpaper-generate-btn"
              onClick={async () => {
                if (!wpPrompt.trim()) {
                  toast.error("Describe the wallpaper scene first.");
                  return;
                }
                setWpGenerating(true);
                try {
                  const { data } = await api.post("/generate/avatar", {
                    prompt: `Cinematic wide landscape wallpaper: ${wpPrompt}. Atmospheric, no people in focus, moody lighting.`,
                  });
                  setForm({ ...form, wallpaper_url: data.avatar_url });
                  toast.success("Wallpaper generated.");
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed.");
                } finally {
                  setWpGenerating(false);
                }
              }}
              disabled={wpGenerating}
              className="btn-pill btn-primary text-xs px-3 py-2 disabled:opacity-50"
            >
              {wpGenerating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {wpGenerating ? "Generating…" : "Generate"}
            </button>
            <button
              type="button"
              data-testid="create-wallpaper-upload-btn"
              onClick={() => wpFileRef.current?.click()}
              className="btn-pill btn-ghost text-xs px-3 py-2"
            >
              <Upload size={12} /> Upload
            </button>
            <input
              ref={wpFileRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (f.size > 6 * 1024 * 1024) return toast.error("Image too large (max 6MB).");
                const reader = new FileReader();
                reader.onload = () => setForm({ ...form, wallpaper_url: reader.result });
                reader.readAsDataURL(f);
              }}
              className="hidden"
              data-testid="create-wallpaper-upload-input"
            />
          </div>
        </Section>

        <Section title="Tags">
          <div className="flex flex-wrap gap-2">
            {form.tags.map((t) => (
              <span key={t} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-neutral-200">
                #{t}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })}
                  className="ml-2 text-neutral-500 hover:text-white"
                >×</button>
              </span>
            ))}
            <input
              data-testid="create-tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
              placeholder="Add tag + Enter"
              className="rounded-full border border-white/10 bg-transparent px-3 py-1 text-xs text-white placeholder:text-neutral-600 focus:border-violet-500/50 focus:outline-none"
            />
          </div>
        </Section>

        <Section title="Visibility">
          <ToggleRow
            testId="create-nsfw-toggle"
            label="Mark as NSFW (18+)"
            hint="Adult chat style will unlock in chats with this character."
            checked={form.is_nsfw}
            onChange={(v) => setForm({ ...form, is_nsfw: v })}
          />
          <ToggleRow
            testId="create-public-toggle"
            label="Public (share on Discover)"
            hint="Uncheck to keep this character private to your account."
            checked={form.is_public}
            onChange={(v) => setForm({ ...form, is_public: v })}
          />
        </Section>

        {error && (
          <div data-testid="create-error" className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={saving}
          data-testid="create-submit-btn"
          className="btn-pill btn-primary w-full py-3.5 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Create character"}
        </button>
      </form>

      <BottomNav />

      <style>{`
        .input-field {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 14px;
          padding: 0.75rem 1rem;
          color: white;
          font-family: 'Outfit', sans-serif;
          outline: none;
          resize: vertical;
        }
        .input-field:focus { border-color: rgba(139,92,246,0.5); background: rgba(255,255,255,0.06); }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
      <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-neutral-500">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-neutral-400">{label}</span>
      {children}
    </label>
  );
}

function ToggleRow({ label, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm text-white">{label}</div>
        {hint && <div className="text-xs text-neutral-500">{hint}</div>}
      </div>
      <Switch data-testid={testId} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/**
 * Textarea that grows to fit its content — no more dragging the corner to
 * read pre-filled text on edit. Height snaps to scrollHeight on every value
 * change (including the initial server-hydrated value in edit mode), with
 * a `minRows`-based floor so it doesn't collapse below its resting size.
 */
function AutoTextarea({ value, onChange, minRows = 3, className, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Snap to fit. Reset to 'auto' first so shrinking (e.g. after deleting a
    // lot of text) also works — otherwise scrollHeight only reports the
    // previous larger value.
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={minRows}
      className={className}
      style={{ overflow: "hidden", resize: "none" }}
      {...rest}
    />
  );
}
